import React, { useMemo } from "react";
import {
  Calendar, Phone, Mail, Car, X, Send, Edit, RotateCcw, Trash, FileText,
  Circle, Zap, Check, StickyNote, Users, Clock, CheckCircle, AlertCircle,
  Search, Filter, UserCog, RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  StatCard, StatusBadge, FilterPill, SectionCard, ActionButton, EmptyState,
  SearchInput, APPOINTMENT_STATUS,
} from "./AdminUI";
import WhatsAppReadyButton from "./WhatsAppReadyButton";

const AppointmentsTab = ({ ctx }) => {
  const {
    upcomingAppointments, loading, filterDate, setFilterDate, filterStatus, setFilterStatus,
    services, technicians, editingAppointment, setEditingAppointment,
    rescheduleForm, setRescheduleForm, emailModal, setEmailModal,
    emailForm, setEmailForm, sendingEmail, confirmingId, fileInputRef,
    timeSlots, durationOptions, fetchAllAppointments, fetchAllBlockedSlots,
    handleUpdateDuration, handleAssignTechnician, handleFileSelect, triggerFileInput,
    handleConfirmAppointment, handleResendConfirmation, openEmailModal, openRescheduleModal,
    handleCancelAppointment, handleReconfirm, handleDeletePermanent, handleReschedule,
    handleSendEmail, formatDate,
  } = ctx;

  /* Local filters */
  const [search, setSearch] = React.useState("");
  const [technicianFilter, setTechnicianFilter] = React.useState("all");

  /* Apply base filter (date + status) then local (search + technician) */
  const list = useMemo(() => {
    let arr = upcomingAppointments || [];
    if (filterDate) arr = arr.filter((a) => a.date === filterDate);
    if (filterStatus !== "all") arr = arr.filter((a) => a.status === filterStatus);
    if (technicianFilter !== "all") {
      arr = arr.filter((a) =>
        technicianFilter === "none" ? !a.technician : a.technician === technicianFilter
      );
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const qCompact = q.replace(/[^a-z0-9]/g, "");
      arr = arr.filter((a) =>
        [a.client_name, a.client_email, a.client_phone, a.client_plate, a.service_name]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
        || (qCompact && (a.client_plate || "").toLowerCase().replace(/[^a-z0-9]/g, "").includes(qCompact))
      );
    }
    return arr;
  }, [upcomingAppointments, filterDate, filterStatus, technicianFilter, search]);

  const counts = useMemo(
    () => ({
      total: (upcomingAppointments || []).length,
      request: (upcomingAppointments || []).filter((a) => a.status === "request").length,
      pending: (upcomingAppointments || []).filter((a) => a.status === "pending").length,
      confirmed: (upcomingAppointments || []).filter((a) => a.status === "confirmed").length,
      cancelled: (upcomingAppointments || []).filter((a) => a.status === "cancelled").length,
    }),
    [upcomingAppointments]
  );

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Total" value={counts.total} color="text-white" accent="bg-white/[0.06]" testid="stat-total" />
        <StatCard icon={AlertCircle} label="À planifier" value={counts.request} color="text-purple-400" accent="bg-purple-500/10" testid="stat-request" />
        <StatCard icon={Clock} label="En attente" value={counts.pending} color="text-amber-400" accent="bg-amber-500/10" testid="stat-pending" />
        <StatCard icon={CheckCircle} label="Confirmés" value={counts.confirmed} color="text-emerald-400" accent="bg-emerald-500/10" testid="stat-confirmed" />
      </div>

      {/* Filters */}
      <SectionCard title="Filtres" icon={Filter} iconColor="text-white/50"
        action={
          <button
            onClick={() => { fetchAllAppointments(); fetchAllBlockedSlots && fetchAllBlockedSlots(); }}
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-medium transition-colors"
          >
            <RefreshCw size={13} /> Actualiser
          </button>
        }
      >
        <div className="flex flex-wrap gap-3 items-center">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher client, plaque, email, service..."
            icon={Search}
          />
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-white/40" />
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 w-40 text-sm"
            />
            {filterDate && (
              <button onClick={() => setFilterDate("")} className="text-white/40 hover:text-white">
                <X size={15} />
              </button>
            )}
          </div>
          <Select value={technicianFilter} onValueChange={setTechnicianFilter}>
            <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 w-44 text-sm">
              <UserCog size={14} className="mr-1.5 text-white/40" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1E] border-white/10">
              <SelectItem value="all">Tous les techniciens</SelectItem>
              <SelectItem value="none">Non assignés</SelectItem>
              {technicians.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { key: "all", label: "Tous" },
            { key: "request", label: "À planifier" },
            { key: "pending", label: "En attente" },
            { key: "confirmed", label: "Confirmés" },
          ].map((s) => (
            <FilterPill key={s.key} active={filterStatus === s.key} onClick={() => setFilterStatus(s.key)}>
              {s.label}
            </FilterPill>
          ))}
        </div>
      </SectionCard>

      {/* List */}
      {loading ? (
        <EmptyState icon={Clock} title="Chargement..." />
      ) : list.length === 0 ? (
        <EmptyState icon={Calendar} title="Aucun rendez-vous trouvé" description="Ajustez les filtres ou créez un nouveau RDV." />
      ) : (
        <div className="space-y-3">
          {list.map((apt) => {
            const statusCfg = APPOINTMENT_STATUS[apt.status] || APPOINTMENT_STATUS.pending;
            const isCancelled = apt.status === "cancelled";
            return (
              <div
                key={apt.id}
                data-testid={`appointment-${apt.id}`}
                className={`bg-[#141414] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.12] transition-all ${isCancelled ? "opacity-60" : ""}`}
              >
                <div className="flex flex-wrap gap-5 p-4">
                  {/* Date Badge */}
                  <div className="shrink-0">
                    {apt.date ? (
                      <div className="bg-gradient-to-br from-[#FF3B30] to-[#D63025] text-white px-3 py-2.5 rounded-lg text-center min-w-[88px] shadow-md shadow-red-500/20">
                        <p className="text-[10px] uppercase tracking-wider opacity-90">{formatDate(apt.date)}</p>
                        <p className="text-xl font-bold leading-tight">{apt.time}</p>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white px-3 py-2.5 rounded-lg text-center min-w-[88px] shadow-md shadow-purple-500/20">
                        <p className="text-[10px] uppercase tracking-wider opacity-90">Sans</p>
                        <p className="text-sm font-bold leading-tight">À planifier</p>
                      </div>
                    )}
                  </div>

                  {/* Service + status */}
                  <div className="min-w-[180px]">
                    <p className="text-white font-semibold">{apt.service_name}</p>
                    <div className="mt-1.5"><StatusBadge config={statusCfg} /></div>
                    {apt.notes && (
                      <div className="flex items-start gap-1.5 mt-2 text-white/40 text-xs italic">
                        <StickyNote size={11} className="mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{apt.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Client */}
                  <div className="flex-1 min-w-[230px] space-y-1">
                    <p className="text-white font-medium">{apt.client_name}</p>
                    <div className="flex items-center gap-1.5 text-white/50 text-[12px] flex-wrap">
                      <Phone size={12} />
                      <a href={`tel:${apt.client_phone}`} className="hover:text-[#FF3B30]">{apt.client_phone}</a>
                      <WhatsAppReadyButton apt={apt} testId={`apt-whatsapp-btn-${apt.id}`} />
                    </div>
                    {apt.client_email && (
                      <div className="flex items-center gap-1.5 text-white/50 text-[12px]">
                        <Mail size={12} />
                        <a href={`mailto:${apt.client_email}`} className="hover:text-[#FF3B30] truncate">{apt.client_email}</a>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-[#FF3B30] font-semibold text-[13px]">
                      <Car size={13} />
                      {apt.client_plate || "N/A"}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                      {apt.tire_size && (
                        <span className="text-amber-400 text-[11px] flex items-center gap-1"><Circle size={10} /> Pneus: {apt.tire_size} {apt.tire_type && `- ${apt.tire_type}`}</span>
                      )}
                      {apt.optim_type && (
                        <span className="text-purple-400 text-[11px] flex items-center gap-1"><Zap size={10} /> {apt.optim_type}</span>
                      )}
                      {apt.entretien_type && (
                        <span className="text-blue-400 text-[11px] flex items-center gap-1"><Check size={10} /> {apt.entretien_type}</span>
                      )}
                      {apt.brake_options && (
                        <span className="text-orange-400 text-[11px] flex items-center gap-1"><Circle size={10} /> {apt.brake_options}</span>
                      )}
                    </div>
                  </div>

                  {/* Duration & Technician */}
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <div>
                      <p className="text-white/35 text-[10px] uppercase tracking-wider mb-1">Durée</p>
                      <Select value={(apt.duration || 60).toString()} onValueChange={(v) => handleUpdateDuration(apt.id, parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs bg-[#1C1C1E] border-white/[0.06] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1E] border-white/10">
                          {durationOptions.map((d) => (
                            <SelectItem key={d} value={d.toString()}>{d} min</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-white/35 text-[10px] uppercase tracking-wider mb-1">Technicien</p>
                      <Select value={apt.technician || "none"} onValueChange={(v) => handleAssignTechnician(apt.id, v === "none" ? "" : v)}>
                        <SelectTrigger className={`h-8 text-xs border ${apt.technician ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400" : "bg-[#1C1C1E] border-white/[0.06] text-white/60"}`}>
                          <SelectValue placeholder="Non assigné" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1E] border-white/10">
                          <SelectItem value="none">Non assigné</SelectItem>
                          {technicians.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Actions row */}
                <div className="flex flex-wrap gap-1.5 px-4 py-2.5 border-t border-white/[0.04] bg-white/[0.015]">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    ref={(el) => (fileInputRef.current[apt.id] = el)}
                    onChange={(e) => handleFileSelect(apt.id, e)}
                    className="hidden"
                  />
                  {apt.status === "request" && (
                    <ActionButton icon={Calendar} label="Planifier" color="purple" testid={`schedule-apt-${apt.id}`}
                      onClick={() => { setEditingAppointment(apt); setRescheduleForm({ date: "", time: "", client_plate: "", service_id: "" }); }}
                    />
                  )}
                  {apt.status === "pending" && (
                    <>
                      <ActionButton icon={FileText} label="+ Devis" color="green" testid={`confirm-with-quote-${apt.id}`}
                        onClick={() => triggerFileInput(apt.id)} disabled={confirmingId === apt.id}
                      />
                      <ActionButton icon={confirmingId === apt.id ? Clock : Send} label="Confirmer" color="blue" testid={`confirm-apt-${apt.id}`}
                        onClick={() => handleConfirmAppointment(apt.id)} disabled={confirmingId === apt.id}
                      />
                    </>
                  )}
                  {apt.status === "confirmed" && apt.client_email && (
                    <ActionButton icon={Send} label="Renvoyer" color="green" onClick={() => handleResendConfirmation(apt.id)} />
                  )}
                  <ActionButton icon={Mail} label="Email" color="cyan" onClick={() => openEmailModal(apt)} />
                  {apt.status !== "cancelled" && (
                    <ActionButton icon={Edit} label="Modifier" color="purple" onClick={() => openRescheduleModal(apt)} />
                  )}
                  {apt.status !== "cancelled" && (
                    <ActionButton icon={X} label="Annuler" color="yellow" testid={`cancel-apt-${apt.id}`}
                      onClick={() => handleCancelAppointment(apt.id)}
                    />
                  )}
                  {apt.status === "cancelled" && (
                    <ActionButton icon={RotateCcw} label="Réactiver" color="green" onClick={() => handleReconfirm(apt.id)} />
                  )}
                  <ActionButton icon={Trash} label="Supprimer" color="red" onClick={() => handleDeletePermanent(apt.id)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule Modal */}
      {editingAppointment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
              <Edit size={18} className="text-purple-400" /> Modifier le rendez-vous
            </h3>
            <div className="mb-4 p-3 bg-white/[0.04] rounded-lg">
              <p className="text-white font-medium">{editingAppointment.client_name}</p>
              <p className="text-sm text-white/50">{editingAppointment.service_name}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Service</label>
                <Select value={rescheduleForm.service_id} onValueChange={(v) => setRescheduleForm({ ...rescheduleForm, service_id: v })}>
                  <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-white h-9 text-sm">
                    <SelectValue placeholder="Changer le service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-white/10">
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.duration} min)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Plaque</label>
                <Input type="text" value={rescheduleForm.client_plate}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, client_plate: e.target.value.toUpperCase() })}
                  placeholder="AB-123-CD"
                  className="bg-[#1C1C1E] border-white/10 text-[#FF3B30] font-bold h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Nouvelle date</label>
                <Input type="date" value={rescheduleForm.date}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                  className="bg-[#1C1C1E] border-white/10 text-white h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Nouvelle heure</label>
                <Select value={rescheduleForm.time} onValueChange={(v) => setRescheduleForm({ ...rescheduleForm, time: v })}>
                  <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-white h-9 text-sm">
                    <SelectValue placeholder="Choisir l'heure" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-white/10">
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setEditingAppointment(null); setRescheduleForm({ date: "", time: "", client_plate: "", service_id: "" }); }}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 px-4 rounded-md text-sm transition-colors"
              >Annuler</button>
              <button
                onClick={() => handleReschedule(editingAppointment.id)}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-md text-sm font-bold transition-colors"
              >Reprogrammer</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
              <Mail size={18} className="text-cyan-400" /> Envoyer un email
            </h3>
            <div className="mb-4 p-3 bg-white/[0.04] rounded-lg">
              <p className="text-white font-medium">{emailModal.client_name}</p>
              <p className="text-sm text-cyan-400">{emailModal.client_email}</p>
              <p className="text-sm text-white/50">{emailModal.service_name}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Sujet</label>
                <Input type="text" value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Sujet de l'email"
                  className="bg-[#1C1C1E] border-white/10 text-white h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Message</label>
                <textarea value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Votre message..." rows={6}
                  className="w-full bg-[#1C1C1E] border border-white/10 text-white text-sm p-3 rounded-md resize-none focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setEmailModal(null); setEmailForm({ subject: "", message: "" }); }}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 px-4 rounded-md text-sm transition-colors"
              >Annuler</button>
              <button onClick={handleSendEmail} disabled={sendingEmail}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-500/50 text-white py-2 px-4 rounded-md text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                {sendingEmail ? <>Envoi...</> : <><Send size={14} /> Envoyer</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsTab;
